// PARAQTA — orталық event delegation.
//
// Inline onclick атрибуттарын алмастыру мақсаты бар:
// HTML-де data-action="<name>" + керекті data-* атрибуттар → handlers map.
//
// Step 8.1-ден бастап біртіндеп толады.
// Telegram OAuth widget талап ететін window.onTelegramAuth — өшірілмейді
// (бұл инлайн callback емес, Telegram widget сценарийі).

import { show, showTab, showTab2, prevPage } from './router.js';
import { toggleTheme } from '../features/theme/index.js';
import { logout } from '../features/auth/index.js';
import {
  showLoginModal, closeLoginModal, devLogin, enterGuest,
} from '../features/auth/login-modal.js';
import {
  openBookPage, showBPTab, hoverBPStar, resetBPStars, pickBPStar,
  submitBPReview, submitBPQuote, submitBPDisc, submitBPJournal, likeReview,
  toggleBPDesc, bpGoalStep, bpSavePages,
} from '../features/book-page/index.js';
// 04.10: «Бүгін» беті өшті → кітап тізімі батырмалары бөлек модульде.
import { saveBookToList, unlistBook } from '../features/book-status/index.js';
import {
  showSubmitModal, closeSubmitModal, submitBook,
  setAdminFilter, adminOpenBook, adminDeleteBook, openTypeaheadResult,
} from '../features/submissions/index.js';
import {
  catalogGoPage, filterGenre,
  catalogTab, catalogSearchClear, catalogFilter,
  openGenre, openAuthor, openAllAuthors, openAllFeatured, openAllNew,
  openCatalogSubmitModal, closeCatalogSubmitModal, submitBookV2,
  catalogBack, genreSort, genreLoadMore, catalogSortOptions, authorShare,
} from '../features/catalog/index.js';
import {
  openReflectionModal, closeReflectionModal,
  setReflRating, saveReflection,
} from '../features/reflection/index.js';
import {
  showGoalModal, closeGoalModal, saveGoal,
} from '../features/goals/index.js';
import {
  setTimerMode, timerStart, timerPause, timerReset,
} from '../features/timer/index.js';
import { loadLeaderTab } from '../features/rankings/index.js';
import { openDawysArticle, closeDawysModal } from '../features/dawys/index.js';
import {
  landingDoSearch, landingOpenBook, landingOpenBookByTitle,
} from '../features/landing/index.js';
import { installPWA } from '../features/pwa.js';
import { copyShareLink, toast } from '../utils/toast.js';
import {
  pomodoroToggle, pageIncrement, pageDecrement,
  readingTabClick, openFeaturedBook, openGroupCard,
  saveQuote, streakDetail, openFamily, openJandos,
  openMarathon, addBook, createGroup,
} from '../features/today-v2/index.js';
import {
  profileBack, profileSettings, copyUrl, openList, openQuote,
  openAchievements, openQuotes, openWrapped, openGoalModal, editName,
  settingsAccount, settingsNotifications, settingsPrivacy,
  settingsSpoiler, settingsJandos, settingsHelp, settingsCopyright,
} from '../features/profile/index.js';
import {
  expandPost, closePostModal, postLike, postSave, revealSpoiler,
  feedTab, feedFilter, feedNewPost, postMenu, postShare, postComments,
  openUser,
} from '../features/feed/index.js';
import {
  checkoutJandos, closeCheckoutModal, jandosBack,
} from '../features/jandos/index.js';
import { marathonBack } from '../features/marathon/index.js';
import { showPremiumLockModal } from '../features/premium-lock/modal.js';
import { publicProfileBack, shareProfile, ppFollow } from '../features/public-profile/index.js';
import {
  wrappedNext, wrappedPrev, wrappedClose,
  wrappedShareTelegram, wrappedShareNative,
} from '../features/wrapped/index.js';
import { api } from './api.js';

const handlers = {
  // ── Step 8.1: Navigation + theme ──
  'navigate': (btn) => show(btn.dataset.target),
  // «Менің сөрем» — 3D сөре беті (кітаптар, күй, баға, оқу тарихы).
  // Жүктелген EPUB/PDF оқу — сөре ішінен bookshelf overlay-мен ашылады.
  'open-shelf': () => show('shelf'),
  // Ф2: Кітапханадан тікелей файл жүктеу (EPUB/PDF).
  'shelf-upload': () => import('../features/bookshelf/index.js').then(m => m.openBookshelf('upload')).catch(() => {}),
  'theme-toggle': () => toggleTheme(),
  'tab': (btn) => showTab(btn.dataset.target, btn),
  'mobile-tab': (btn) => showTab2(btn.dataset.target, btn),

  // ── v2 Бүгін: Pomodoro + page counter + reading tabs ──
  'pomodoro-toggle': () => pomodoroToggle(),
  'page-increment': () => pageIncrement(),
  'page-decrement': () => pageDecrement(),
  'reading-tab-click': (btn) => readingTabClick(btn.dataset.idx),
  'open-featured-book': () => openFeaturedBook(),
  'open-group': (btn) => openGroupCard(btn.dataset.groupId),
  'save-quote': () => saveQuote(),
  'streak-detail': () => streakDetail(),
  'open-family': () => openFamily(),
  'open-jandos': () => openJandos(),
  'open-marathon': () => openMarathon(),
  'add-book': () => addBook(),
  'create-group': () => createGroup(),

  // ── Профиль v2 ──
  'profile-back': () => profileBack(),
  'profile-settings': () => profileSettings(),
  // Stage 7.5: барлық деңгейлер modal (founder beta feedback)
  'open-levels': () => import('../features/profile/levels-modal.js').then(m => m.showLevelsModal()),
  // Stage 7.5: Avatar upload (Profile hero)
  'avatar-pick': () => {
    const inp = document.querySelector('[data-field="avatar-input"]');
    if (!inp) return;
    if (!inp._bound) {
      inp._bound = true;
      inp.addEventListener('change', async (ev) => {
        const file = ev.target.files && ev.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
          alert('Тек сурет файлдары');
          return;
        }
        // Resize to 256x256 via canvas
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = async () => {
          const SZ = 256;
          const cnv = document.createElement('canvas');
          cnv.width = SZ; cnv.height = SZ;
          const ctx = cnv.getContext('2d');
          // Square crop center
          const src = Math.min(img.width, img.height);
          const sx = (img.width - src) / 2;
          const sy = (img.height - src) / 2;
          ctx.drawImage(img, sx, sy, src, src, 0, 0, SZ, SZ);
          const dataUrl = cnv.toDataURL('image/jpeg', 0.86);
          URL.revokeObjectURL(url);
          try {
            const { api } = await import('./api.js');
            const res = await api('/api/profile/avatar', {
              method: 'POST',
              body: JSON.stringify({ avatar: dataUrl }),
            });
            if (res?.ok) {
              // Immediate UI update
              const avEl = document.querySelector('[data-field="user-avatar"]');
              if (avEl) {
                avEl.style.backgroundImage = `url(${dataUrl})`;
                avEl.style.color = 'transparent';
                avEl.textContent = '';
              }
              const t = await import('../utils/toast.js').catch(() => null);
              if (t?.toast) t.toast('Аватар жаңартылды ✓');
            } else {
              alert('Сақталмады');
            }
          } catch (e) {
            console.warn('avatar save failed', e);
            alert('Қате');
          }
        };
        img.onerror = () => { URL.revokeObjectURL(url); alert('Сурет оқылмады'); };
        img.src = url;
        // reset input so same file can re-trigger
        ev.target.value = '';
      });
    }
    inp.click();
  },
  // Stage 7.5: Шрифт өлшемі (accessibility — үлкен жасты adamдар)
  'set-font-scale': (btn) => {
    const scale = parseInt(btn.dataset.scale, 10) || 100;
    // Stage 7.5: zoom CSS property — px-те жазылған бүкіл интерфейс үшін
    // (root font-size px-ке әсер етпейді, zoom бәрін біркелкі үлкейтеді).
    // Chrome/Edge/Safari толық қолдайды; Firefox 126+ — да жұмыс істейді.
    document.body.style.zoom = (scale / 100).toString();
    try { localStorage.setItem('paraqta:font_scale', String(scale)); } catch (_) {}
    document.querySelectorAll('.font-scale-btn').forEach(b => {
      b.style.background = (parseInt(b.dataset.scale,10) === scale)
        ? 'var(--ac)' : 'color-mix(in srgb, var(--ink) 5%, transparent)';
      b.style.color = (parseInt(b.dataset.scale,10) === scale) ? '#fff' : 'var(--ink)';
    });
  },
  'copy-url': () => copyUrl(),
  'open-list': (btn) => openList(btn.dataset.status),
  'open-quote': () => openQuote(),
  'open-achievements': () => openAchievements(),
  'open-quotes': () => openQuotes(),
  'open-wrapped': () => openWrapped(),
  'open-goal-modal': () => openGoalModal(),
  'edit-name': () => editName(),
  // Stage 7.5 Session 3: Monthly Wrapped
  'open-wrapped-monthly-latest': () => {
    try { window.location.hash = ''; } catch (_) {}
    show('wrapped-monthly');
  },
  'open-wrapped-monthly': (btn) => {
    const y = btn.dataset.year, m = btn.dataset.month;
    try { window.location.hash = `wrapped-monthly/${y}/${m}`; } catch (_) {}
    show('wrapped-monthly');
  },
  'monthly-wrapped-share': () => import('../features/monthly-wrapped/index.js').then(m => m.shareMonthlyWrapped()),

  // ── Wrapped 2025 (Stage 6.9.5) ──
  'wrapped-next': () => wrappedNext(),
  'wrapped-prev': () => wrappedPrev(),
  'wrapped-close': () => wrappedClose(),
  'wrapped-share-telegram': () => wrappedShareTelegram(),
  'wrapped-share-native': () => wrappedShareNative(),
  // Stage 7.5: Wrapped slide-ті PNG-ге сақтау (iPhone "Сурет сақтау")
  'wrapped-share-png': () => import('../features/wrapped/index.js').then(m => m.wrappedSharePng()),
  'open-wardrobe': () => import('../features/wardrobe/index.js').then(m => m.openWardrobe()).catch(() => {}),
  'settings-account': () => settingsAccount(),
  'settings-notifications': () => settingsNotifications(),
  'settings-privacy': () => settingsPrivacy(),
  'settings-spoiler': () => settingsSpoiler(),
  'settings-jandos': () => settingsJandos(),
  'settings-help': () => settingsHelp(),
  'settings-copyright': () => settingsCopyright(),

  // ── Каталог v2 ──
  'catalog-tab': (btn) => catalogTab(btn.dataset.tab),
  'catalog-search-clear': () => catalogSearchClear(),
  'catalog-filter': () => catalogFilter(),
  'open-genre': (btn) => openGenre(btn.dataset.genre),
  'open-author': (btn) => openAuthor(btn.dataset.author),
  'open-all-authors': () => openAllAuthors(),
  'open-all-featured': () => openAllFeatured(),
  'open-all-new': () => openAllNew(),
  'open-submit-modal': () => openCatalogSubmitModal(),
  'submit-modal-close': () => closeCatalogSubmitModal(),
  'submit-modal-close-bg': () => closeCatalogSubmitModal(),
  'submit-modal-submit': () => submitBookV2(),

  // ── Каталог v2 Stage 6.3: Жанр + Автор беттері ──
  'catalog-back': () => catalogBack(),
  'catalog-sort-options': () => catalogSortOptions(),
  'genre-sort': (btn) => genreSort(btn.dataset.sort),
  'genre-load-more': () => genreLoadMore(),
  'author-share': () => authorShare(),

  // ── Жан дос (Stage 6.7) ──
  'jandos-back': () => jandosBack(),
  'checkout-jandos': (btn) => checkoutJandos(btn.dataset.planId),
  'close-jandos-modal': () => closeCheckoutModal(),

  // ── Marathon (Stage 6.9.2) ──
  'marathon-back': () => marathonBack(),

  // ── Феед v2 (Stage 6.4) ──
  'feed-tab': (btn) => feedTab(btn.dataset.feedTab),
  'feed-filter': () => feedFilter(),
  'feed-new-post': () => feedNewPost(),
  'expand-post': (btn) => expandPost(btn.dataset.postType, btn.dataset.postId),
  'close-post-modal': () => closePostModal(),
  'post-like': (btn) => postLike(btn.dataset.postType, btn.dataset.postId),
  'modal-post-like': (btn) => postLike(btn.dataset.postType, btn.dataset.postId),
  'post-save': (btn) => postSave(btn.dataset.postType, btn.dataset.postId),
  'modal-post-save': (btn) => postSave(btn.dataset.postType, btn.dataset.postId),
  'post-comments': () => postComments(),
  'post-share': () => postShare(),
  'post-menu': () => postMenu(),
  'reveal-spoiler': (btn) => revealSpoiler(btn),
  'open-user': (btn) => openUser(btn.dataset.userId),
  'modal-open-book': (btn) => { closePostModal(); if (btn.dataset.slug) openBookPage(btn.dataset.slug); },

  // ── Step 8.2: Auth + Login modal ──
  'show-login': () => showLoginModal(),
  // Рейтинг v2: КІМ жарысады қойындылары (Оқырмандар · Клубтар · Мектептер)
  'leaders-entity': (btn) => {
    import('../features/rankings/index.js')
      .then((m) => m.setLeadersEntity(btn.dataset.ent, btn))
      .catch(() => {});
  },
  'close-login': () => closeLoginModal(),
  'dev-login': () => devLogin(),
  'enter-guest': () => enterGuest(),
  // enterGuest әдетте show('catalog') автоматты шақырады. Жаңа landing top
  // nav-та "Каталог"/"Дауыс" сияқты әр түрлі target керек — guest-ке
  // өткеннен кейін data-target бойынша page-ке өту.
  'enter-guest-target': (btn) => { enterGuest(); show(btn.dataset.target); },
  'logout': () => logout(),
  // 22.09: «Ақау таптым» — профиль → баптаулар. Хабар founder-дің ботына кетеді.
  'bug-report': () => {
    import('../features/bug-report/index.js').then(m => m.openBugReport()).catch(() => {});
  },

  // ── Step 8.3: Book actions ──
  'open-book': (btn) => openBookPage(btn.dataset.slug),
  // Кітап бөлісу — Web Share API (мобильде) немесе clipboard (десктопта)
  'book-share': async (btn) => {
    const url   = btn.dataset.url || '';
    const title = btn.dataset.title || 'Кітап';
    const author = btn.dataset.author || '';
    if (!url) return;
    const text = author ? `${title} — ${author}` : title;
    try {
      if (navigator.share) {
        await navigator.share({ title: text, text: `PARAQTA-да оқыңыз: ${text}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        // Toast
        const t = document.createElement('div');
        t.textContent = '🔗 Сілтеме көшірілді';
        t.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:#1C1A16;color:#fff;padding:10px 20px;border-radius:999px;font-size:13px;font-weight:600;z-index:9999;pointer-events:none;';
        document.body.appendChild(t);
        setTimeout(() => t.remove(), 2200);
      }
    } catch (e) {
      // Fallback: URL бар болса сілтеме ашу
      try { await navigator.clipboard.writeText(url); } catch (_) {}
    }
  },
  // Stage 7.5 Bug 1: book page back — соңғы persistable page-ке оралу
  // Артқа — НАҚТЫ алдыңғы бетке. Бұрын тек localStorage 'last_page'-ті
  // оқитын, ал онда жанр/автор беттері САҚТАЛМАЙДЫ → адам жанрдан кітап
  // ашып, артқа басқанда каталогқа лақтырылатын (founder байқады).
  // Енді router.prevPage() басым — ол нақты алдыңғы бетті есте ұстайды.
  'book-back': () => {
    // 04.10: dash/books/popular/search/cities өшті — тізімде жоқ.
    const valid = ['oqu','catalog','profile','groups','feed','jandos','marathon',
                   'leaders','genre','author','shelf'];
    let prev = '';
    try { prev = prevPage() || ''; } catch (_) {}
    if (!valid.includes(prev)) {
      try { prev = localStorage.getItem('paraqta:last_page') || ''; } catch (_) {}
    }
    show(valid.includes(prev) ? prev : 'catalog');
  },
  // Кітап бетінен: қордағы кітапты сөреге қосып, бірден оқуға өту.
  // ❗Қонақ басса — логин ашылады да, кіргеннен кейін кітап ӨЗІ ашылады.
  // Бұрын 401 «Авторизациялан» тост болып шығатын да, адам тұйыққа тірелетін
  // (кітап беттеріне аптасына ~946 адам келеді). library-read модулін қара.
  'lib-read': async (btn) => {
    const m = await import('../features/library-read/index.js');
    await m.startLibraryRead(btn.dataset.title || '', btn.dataset.author || '', btn);
  },
  // 25.09: мәтіні жоқ кітап бетіндегі үш жол (book-page `bp-own` блогы).
  'bp-own-file': async () => {
    try { (await import('./track.js')).track('own_copy_ask', { choice: 'file', from: 'bookpage' }); } catch (_) {}
    if (!window.currentUser) { (await import('../features/auth/login-modal.js')).showLoginModal(); return; }
    const m = await import('../features/bookshelf/index.js');
    await m.openBookshelf('upload');
  },
  'save-book': (btn) => saveBookToList(
    btn.dataset.title, btn.dataset.author,
    parseInt(btn.dataset.pages, 10) || 0,
    parseInt(btn.dataset.status, 10), btn,
  ),
  // Step BP3: hero-да белсенді status батырмасын қайта басу — toggle off.
  'unlist-book': (btn) => unlistBook(btn.dataset.title, btn),
  'bp-goal-step': (btn) => bpGoalStep(btn.dataset.bookId, parseInt(btn.dataset.delta, 10)),
  'bp-save-pages': (btn) => bpSavePages(btn.dataset.bookId),

  // Step BP4-rev: User кітап ұсыну + admin recent additions.
  'show-submit-modal': () => showSubmitModal(),
  'close-submit-modal': () => closeSubmitModal(),
  'submit-book': () => submitBook(),
  'open-typeahead': (btn) => openTypeaheadResult(btn.dataset.slug),
  'admin-filter': (btn) => setAdminFilter(btn.dataset.source),
  'admin-open-book': (btn) => adminOpenBook(btn.dataset.slug),
  'admin-delete-book': (btn) => adminDeleteBook(parseInt(btn.dataset.bookId, 10), btn.dataset.title, btn),
  // Step BP4-rev: Top Nav admin shield button — show('admin') router.
  'open-admin': () => show('admin'),
  'pick-star': (btn) => pickBPStar(parseInt(btn.dataset.star, 10)),
  'submit-review': (btn) => submitBPReview(btn.dataset.title),
  'submit-quote': (btn) => submitBPQuote(btn.dataset.title),
  'submit-disc': (btn) => submitBPDisc(btn.dataset.title),
  'submit-journal': (btn) => submitBPJournal(btn.dataset.title),
  'like-review': (btn) => likeReview(parseInt(btn.dataset.reviewId, 10), btn),
  'bp-tab': (btn) => showBPTab(btn.dataset.tab, btn, btn.dataset.title, btn.dataset.slug),
  'copy-share': (btn) => copyShareLink(btn.dataset.url),
  'catalog-page': (btn) => catalogGoPage(parseInt(btn.dataset.page, 10)),
  'filter-genre': (btn) => filterGenre(btn.dataset.genre || null),
  'genre-tag': (btn) => { filterGenre(btn.dataset.genre); show('catalog'); },
  'open-reflection': (btn) => openReflectionModal(parseInt(btn.dataset.bookId, 10), btn.dataset.title),
  'spoiler-reveal': (btn) => btn.classList.remove('spoiler'),
  // Step BP1.1: Кітап бетіндегі сипаттама blok-ы — "Толық оқу" ↔ "Қысқарту".
  'toggle-desc': (btn) => toggleBPDesc(btn),

  // ── Step 8.4: Reflection + Goals + Timer ──
  'close-reflection': () => closeReflectionModal(),
  'set-refl-rating': (btn) => setReflRating(parseInt(btn.dataset.rating, 10)),
  'save-reflection': () => saveReflection(),
  'show-goal-modal': () => showGoalModal(),
  'close-goal-modal': () => closeGoalModal(),
  'save-goal': () => saveGoal(),
  'timer-start': () => timerStart(),
  'timer-pause': () => timerPause(),
  'timer-reset': () => timerReset(),
  'timer-mode': (btn) => setTimerMode(btn.dataset.mode, btn),

  // ── Step 8.5: Search + Дауыс + Rankings + Groups ──
  'leader-tab': (btn) => loadLeaderTab(btn.dataset.tab, btn),
  'open-dawys': (btn) => openDawysArticle(parseInt(btn.dataset.id, 10)),
  'close-dawys': () => closeDawysModal(),
  // Step N5: Article modal "Кітап туралы" → smooth close (320ms) →
  // openBookPage(slug). Гибрид архитектура — pg-bookpage толық бет.
  'open-book-from-modal': (btn) => {
    const slug = btn.dataset.slug;
    if (!slug) return;
    closeDawysModal();
    setTimeout(() => openBookPage(slug), 320);
  },
  // dawys overlay backdrop — тек overlay-нің өзіне click болғанда жабу
  'close-dawys-bg': (btn, e) => { if (e.target === btn) closeDawysModal(); },
  'join-group': async (btn) => {
    const invite = btn.dataset.invite;
    if (!invite) return;
    // 27.08: шақыру кодымен САЙТТЫҢ ӨЗІНДЕ қосыламыз. Бұрын бұл әрекет
    // t.me/oyordabot-ты ашатын, сондықтан клубқа тек Telegram арқылы
    // кіруге болатын — founder «сайттан да қосыла беретін қылмаймыз ба»
    // деп байқады. Telegram-ға өту тек резервте қалды (401 = логин жоқ).
    try {
      const r = await fetch('/api/groups/join-by-code', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: invite }),
      });
      if (r.ok) {
        const res = await r.json();
        if (res && res.gid) {
          toast(res.status === 'joined'
            ? `«${res.name}» клубына қосылдың ✓` : 'Сен бұл клубтың мүшесісің');
          window._pendingGroupOpen = res.gid;
          show('groups');
          return;
        }
      }
      if (r.status === 401) {
        // Логин жоқ — кодты сақтаймыз, кіргеннен кейін өзі қосылады.
        try { localStorage.setItem('pq_join_code', invite); } catch (_) {}
        show('groups');
        return;
      }
      toast('Шақыру ескірген немесе қате');
    } catch (_) {
      toast('Байланыс жоқ — кейінірек қайтала');
    }
  },

  // ── Premium triggers (Stage 6.9.3) ──
  'open-ai-chat': () => showPremiumLockModal('ai-chat'),

  // ── Public profile (Stage 6.9.4) ──
  'pp-back': () => publicProfileBack(),
  'pp-share': (btn) => shareProfile(btn.dataset.url),
  'pp-follow': (btn) => ppFollow(btn),
  'edit-custom-slug': async (btn) => {
    const { showCustomSlugModal } = await import('../features/public-profile/slug-modal.js');
    showCustomSlugModal(btn.dataset.currentSlug || '');
  },

  // ── Step 8.6: Landing + misc ──
  'landing-search': () => landingDoSearch(),
  'open-book-landing': (btn) => landingOpenBook(btn.dataset.slug),
  'open-book-landing-by-title': (btn) => landingOpenBookByTitle(btn.dataset.title),
  'install-pwa': () => installPWA(),
  'nfc-bookmarks': () => import('../features/oqu/index.js').then(m => m.openNfcBookmarks()).catch(() => {}),
  'lamp-devices': () => import('../features/lamp/index.js').then(m => m.openLampPanel()).catch(() => {}),

  // ── Feed onboarding (Step N1): "Түсіндім" → localStorage save + fade-out ──
  // 26.08: кітапхана бас жолындағы «N кітапты тегін оқисың» → оқылатын
  // бөлімге апарады (адам ашылмайтын карточкаларды шарлап әуре болмасын).
  'scroll-readable': () => {
    const s = document.querySelector('#pg-catalog [data-section="readable"]')
           || document.querySelector('#pg-catalog [data-section="ertegi"]');
    if (s) s.scrollIntoView({ behavior: 'smooth', block: 'start' });
  },
  'dismiss-onboarding': () => {
    localStorage.setItem('paraqta_feed_onboarded', 'true');
    const ob = document.getElementById('feedOnboarding');
    if (!ob) return;
    ob.style.transition = 'opacity 300ms ease, transform 300ms ease';
    ob.style.opacity = '0';
    ob.style.transform = 'translateY(-10px)';
    setTimeout(() => ob.remove(), 300);
  },

  // `_stop` — closest('[data-action]') outer контейнерге жетпес үшін.
  // event.stopPropagation() inline pattern-нің орнын алмастырады.
  '_stop': () => {},
};

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-action]');
  if (!btn) return;

  const action = btn.dataset.action;
  const handler = handlers[action];
  if (!handler) return;

  e.preventDefault();
  handler(btn, e);
});

// Star rating hover (book-page review form)
document.addEventListener('mouseover', (e) => {
  const star = e.target.closest('[data-hover-star]');
  if (star) hoverBPStar(parseInt(star.dataset.hoverStar, 10));
});

// mouseleave — bubble болмайды, capture phase-та ұстаймыз
document.addEventListener('mouseleave', (e) => {
  if (e.target?.id === 'bpStars') resetBPStars();
}, true);
